import { Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Products } from 'enums';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiDatePicker from 'features/ui/base-components/pai-date-picker';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiSelect from 'features/ui/base-components/pai-select';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { License } from '../../../../types/licenses.types';
import useMapProductNames from './use-map-product-names';

interface Props {
  open: boolean;
  loading: boolean;
  license: License | null;

  products: string[];
  selectedProduct: string;
  selectedBillingType: 'monthly' | 'quarterly' | 'yearly' | 'one_time';

  credits: number;
  expirationDate: Date;

  handleProductChange(event: React.ChangeEvent<HTMLInputElement>): void;
  handleBillingTypeChange(event: React.ChangeEvent<HTMLInputElement>): void;
  handleCreditsChange(event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>): void;
  handleExpDateChange(date: unknown): void;

  handleCreateNewLicense(): void;
  handleUpdateLicense(): void;
  handleClose(): void;
}

const BILLING_TYPE_LABELS: Record<string, string> = {
  monthly: 'Monthly',
  yearly: 'Yearly',
  one_time: 'One-time',
};

const AdminLicenseDialog: React.FC<Props> = ({
  open,
  license,
  loading,
  selectedProduct,
  selectedBillingType,
  products,
  credits,
  expirationDate,
  handleProductChange,
  handleBillingTypeChange,
  handleCreditsChange,
  handleExpDateChange,
  handleCreateNewLicense,
  handleUpdateLicense,
  handleClose,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const downLG = useMediaQuery(theme.breakpoints.down('lg'));

  const mapProductName = useMapProductNames();

  const isUpdateLicense = !_.isNil(license);

  const filteredProducts = useMemo(
    () => products,
    [products]
  );

  const isCreditProduct = [Products.MOTIVE, Products.LEAD].includes(selectedProduct as Products);

  const isDisabled = useMemo(() => {
    return selectedProduct === '';
  }, [selectedProduct]);

  return (
    <Dialog classes={{ paper: classes.paper }} open={open} fullScreen={downLG} fullWidth={true}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{isUpdateLicense ? t('editLicense') : t('newLicense')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={2}>
          {!_.isEmpty(filteredProducts) ? (
            <PaiSelect label={t('product')} value={selectedProduct} onChange={handleProductChange} disabled={isUpdateLicense}>
              {filteredProducts.map((product) => (
                <PaiMenuItem key={product} value={product}>
                  {mapProductName(product as Products)}
                </PaiMenuItem>
              ))}
            </PaiSelect>
          ) : null}
          <PaiSelect label={t('billingType') || 'Billing Type'} value={selectedBillingType} onChange={handleBillingTypeChange}>
            {['monthly', 'yearly', 'one_time'].map((bt) => (
              <PaiMenuItem key={bt} value={bt}>
                {BILLING_TYPE_LABELS[bt]}
              </PaiMenuItem>
            ))}
          </PaiSelect>
          {isCreditProduct && (
            <PaiTextField label={t('credits') || 'Credits'} value={credits} type={'number'} onChange={handleCreditsChange} />
          )}
          <PaiDatePicker
            label={t('expDate')}
            value={expirationDate}
            minDate={new Date().setDate(new Date().getDate() + 1)}
            onChange={handleExpDateChange}
          />
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'sm'} underline={'always'} action={handleClose} />
        <PaiButton
          text={isUpdateLicense ? t('save') : t('create')}
          border={'rounded'}
          size={'small'}
          action={isUpdateLicense ? handleUpdateLicense : handleCreateNewLicense}
          disabled={isDisabled}
          loading={loading}
        />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default AdminLicenseDialog;
